import React, { useState, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import { PersonAdd } from '@mui/icons-material';
import { useAppSelector } from 'hooks';
import { UserRole, StorageKey } from 'types/enums';
import { storage } from 'utils';
import { ScreenTitle } from 'components';
import { useTheme } from 'hooks';
import { tokens } from 'theme';
import { config } from 'packages/config';

export const PartnerConnect: React.FC = () => {
  const [userEmail, setUserEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const userData = useAppSelector(({ user }) => user.data);

  const linkUser = useCallback(async () => {
    if (!userEmail.trim()) {
      setMessage({ type: 'error', text: 'Please enter a user email' });

      return;
    }

    setIsLoading(true);
    setMessage(null);

    try {
      // Extract partner ID from user data
      const partnerId = (userData as any)?.partnerId || userData?.username || userData?.id;
      const partnerToken = await storage.get(StorageKey.PARTNER_TOKEN);

      if (!partnerId || !partnerToken) {
        throw new Error('Partner credentials not found');
      }

      const response = await fetch(
        `${config.ENV.API.EVERBEAT_BASE_URL}/partner/link-user/${partnerId}/${partnerToken}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            user_email: userEmail.trim(),
          }),
        },
      );

      // Handle network errors or non-JSON responses
      let result;
      try {
        result = await response.json();
      } catch (e) {
        if (!response.ok) {
          throw new Error(`Server error: ${response.status} ${response.statusText}`);
        }
        throw new Error('Invalid response from server');
      }

      // Check if the response indicates success or failure
      if (result.success === false || result.error) {
        // Handle specific error cases
        if (result.message?.includes('not found') || result.error?.includes('not found')) {
          throw new Error(`User with email ${userEmail} not found`);
        } else if (result.message?.includes('already linked') || result.error?.includes('already linked')) {
          throw new Error(`User ${userEmail} is already linked to this partner account`);
        } else {
          throw new Error(result.message || result.error || 'Failed to link user');
        }
      }

      // Success case
      setMessage({ type: 'success', text: result.message || `Successfully linked user: ${userEmail}` });
      setUserEmail('');
    } catch (error) {
      console.error('Error linking user:', error);
      setMessage({
        type: 'error',
        text: error instanceof Error ? error.message : 'Failed to link user',
      });
    } finally {
      setIsLoading(false);
    }
  }, [userEmail, userData]);

  if (userData?.role !== UserRole.PARTNER) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity='error'>Access denied. This page is only available for partner accounts.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <ScreenTitle title='Partner Connect' />

      <Box sx={{ mt: 3 }}>
        <Typography variant='h6' sx={{ mb: 2 }}>
          Connect users to your partner account
        </Typography>

        <Card sx={{ maxWidth: 600 }}>
          <CardContent>
            {message && (
              <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
                {message.text}
              </Alert>
            )}

            <Stack spacing={3}>
              <Typography variant='body1' color='text.secondary'>
                Enter the email address of the user you want to link to your partner account.
              </Typography>

              <TextField
                fullWidth
                label='User Email'
                type='email'
                value={userEmail}
                onChange={(e) => setUserEmail(e.target.value)}
                disabled={isLoading}
                placeholder='user@example.com'
              />

              <Button
                variant='contained'
                onClick={linkUser}
                disabled={isLoading || !userEmail.trim()}
                startIcon={isLoading ? <CircularProgress size={20} /> : <PersonAdd />}
                sx={{ alignSelf: 'flex-start' }}
              >
                {isLoading ? 'Linking...' : 'Link User'}
              </Button>
            </Stack>
          </CardContent>
        </Card>

        <Box sx={{ mt: 3 }}>
          <Divider sx={{ mb: 2 }} />
          <Typography variant='body2' color='text.secondary'>
            <strong>Company Name:</strong> {(userData as any)?.name || userData?.firstName || 'Partner Company'}
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            <strong>Partner ID:</strong> {(userData as any)?.partnerId || userData?.username || userData?.id}
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            <strong>Status:</strong> {(userData as any)?.partnerStatus || 'active'}
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            <strong>Contact Email:</strong> {userData?.email}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
